import { useEffect, useState } from 'react';
import type { HelpTour as HelpTourDef } from './model';
import { useHelpHighlight } from './useHelpHighlight';

/**
 * Hand-rolled tour stepper (ADR-0066: no tour library in Wave 1). Each step rings its control
 * through the shared highlight overlay and shows the step text in a caption box pinned to the
 * bottom of the help panel. A step whose control is not mounted is skipped with a one-line note.
 */
export function HelpTour({ tour, onExit }: { tour: HelpTourDef; onExit: () => void }) {
  const { highlight, clear, isMounted } = useHelpHighlight();
  const steps = tour.steps;

  /** First mounted step index from `from` going `dir`, collecting the skipped ones. */
  const seek = (from: number, dir: 1 | -1): { index: number | null; skipped: number[] } => {
    const skipped: number[] = [];
    for (let i = from; i >= 0 && i < steps.length; i += dir) {
      if (isMounted(steps[i]!.ref)) return { index: i, skipped };
      skipped.push(i);
    }
    return { index: null, skipped };
  };

  const [state, setState] = useState(() => {
    const first = seek(0, 1);
    return { index: first.index, skipped: first.skipped };
  });
  const index = state.index;

  useEffect(() => {
    if (index != null) highlight(steps[index]!.ref);
  }, [index, steps, highlight]);

  useEffect(() => clear, [clear]);

  const go = (dir: 1 | -1) => {
    if (index == null) return;
    const next = seek(index + dir, dir);
    if (next.index == null) {
      if (dir === 1) onExit();
      return;
    }
    setState(next);
  };

  const note =
    state.skipped.length > 0
      ? `Skipped step ${state.skipped.map((i) => i + 1).join(', ')}: not on this screen right now.`
      : null;
  const hasPrev = index != null && seek(index - 1, -1).index != null;
  const isLast = index == null || seek(index + 1, 1).index == null;

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-10 border-t-2 border-phblue-500 bg-white p-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
      role="dialog"
      aria-label={`Tour: ${tour.title}`}
      data-testid="help-tour"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold text-slate-800">{tour.title}</span>
        <span className="flex shrink-0 gap-1" aria-hidden="true">
          {steps.map((s, i) => (
            <span
              key={`${s.ref}-${i}`}
              className={'h-1.5 w-1.5 rounded-full ' + (i === index ? 'bg-phblue-600' : 'bg-slate-300')}
            />
          ))}
        </span>
      </div>
      <p className="mt-1.5 text-sm text-slate-800" data-testid="help-tour-text">
        {index != null ? steps[index]!.text : 'None of this tour’s controls are on the screen right now.'}
      </p>
      {note && <p className="mt-1 text-[11px] italic text-slate-500">{note}</p>}
      <div className="mt-2 flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={!hasPrev}
          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 disabled:opacity-40"
        >
          Back
        </button>
        {index != null && (
          <button
            type="button"
            onClick={() => go(1)}
            className="rounded bg-phblue-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-phblue-700"
          >
            {isLast ? 'Done' : 'Next'}
          </button>
        )}
        <button
          type="button"
          onClick={onExit}
          className="rounded px-2 py-0.5 text-[11px] text-slate-500 hover:text-slate-800"
        >
          Exit
        </button>
      </div>
    </div>
  );
}
